'use client';

import clsx from 'clsx';
import { useState } from 'react';
import { motion } from 'framer-motion';
import ServiceCategoryCarousel from '@/components/ServiceCategoryCarousel';
import TextLinkButton from '@/components/ui/TextLinkButton';
import type { ServicesTabVM } from '@/lib/services-section-vm';

export type ServicesTheme = 'light' | 'dark';

export type ServicesProps = {
    sectionHeading: string;
    tabs: ServicesTabVM[];
    theme?: ServicesTheme;
};

export default function Services({ sectionHeading, tabs, theme = 'light' }: ServicesProps) {
    const [activeTab, setActiveTab] = useState(0);
    const active = tabs[activeTab];

    if (!active || tabs.length === 0) return null;

    const isDark = theme === 'dark';

    return (
        <div className="mx-auto w-full max-w-7xl">
            {sectionHeading ? (
                <h2
                    className={clsx(
                        'mb-10 max-w-lg text-xl font-medium leading-snug md:mb-20 md:text-2xl md:leading-normal',
                        isDark && 'text-primary',
                    )}
                >
                    {sectionHeading}
                </h2>
            ) : null}
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-8 lg:gap-12 xl:gap-14">
                <div className="relative mb-4 w-full shrink-0 md:mb-0 md:w-[34%] md:max-w-[22rem] lg:max-w-[24rem]">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab.heading}
                            className={clsx(
                                'border-b transition-opacity duration-300',
                                isDark ? 'border-primary/20' : 'border-black/15',
                                {
                                    'opacity-100': activeTab === index,
                                    'opacity-30 hover:opacity-50': activeTab !== index,
                                },
                            )}
                        >
                            <button
                                type="button"
                                onClick={() => setActiveTab(index)}
                                aria-expanded={activeTab === index}
                                className="w-full cursor-pointer pt-5 pb-0 text-left md:pt-6"
                            >
                                <h3
                                    className={clsx(
                                        'text-2xl font-medium uppercase md:text-7xl md:leading-[1.3]',
                                        isDark && 'text-primary',
                                    )}
                                >
                                    {tab.heading}
                                </h3>
                            </button>
                            <motion.div
                                initial={false}
                                animate={{
                                    height: activeTab === index ? 'auto' : 0,
                                    opacity: activeTab === index ? 1 : 0,
                                }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className="overflow-hidden"
                            >
                                <p
                                    className={clsx(
                                        'mt-3 pb-4 text-base leading-relaxed md:mt-4 md:pb-5',
                                        isDark && 'text-primary/90',
                                    )}
                                >
                                    {tab.description}
                                </p>
                                <div className="pb-5 md:pb-6">
                                    <TextLinkButton
                                        text="zobacz więcej"
                                        primaryColor={isDark ? '#EDE3DA' : '#111'}
                                        hoverColor="#803320"
                                        href={tab.href}
                                    />
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>

                <div className="relative min-w-0 flex-1 overflow-visible">
                    <ServiceCategoryCarousel
                        cards={active.cards}
                        categoryDescription={active.categoryDescription}
                        variant="right-bleed"
                        theme={theme}
                        idPrefix={`services-tab-${activeTab}`}
                    />
                </div>
            </div>
        </div>
    );
}
